import { z } from 'zod';

export enum ListingActivityField {
  TITLE = 'title',
  SUMMARY = 'summary',
  DESCRIPTION = 'description',
  CATEGORY = 'category',
  PRICING = 'pricing',
  PHOTOS = 'photos',
  LOCATION = 'location',
  VIBES = 'vibes',
  HOST = 'host',
  BUTTON_LABEL = 'buttonLabel',
  REMINDERS = 'reminders',
  CANCELLATION_POLICY = 'cancellationPolicy',
  RESCHEDULE_POLICY = 'reschedulePolicy',
  EMAIL_MESSAGE = 'emailMessage',
  NOTIFICATION_EMAILS = 'notificationEmails',
  MAP_RESOURCES = 'mapResources',
  RESOURCE_MAP_IMAGE = 'resourceMapImage',
  RESOURCE_GROUP = 'resourceGroup',
  RESOURCE_GROUP_ORDER = 'resourceGroupOrder',
  RESOURCE_CAPACITY = 'resourceCapacity',
  RESOURCES = 'resources',
  INCLUDED_ITEMS = 'includedItems',
  PAYMENT_METHODS = 'paymentMethods',
  TAXES = 'taxes',
  ROOM_CHARGE_VALIDATION = 'roomChargeValidation',
  MEMBER_NUMBER_VALIDATION = 'memberNumberValidation',
  COMPLIMENTARY_BOOKING_VALIDATION = 'complimentaryBookingValidation',
  AVAILABILITY = 'availability',
  CAPACITY = 'capacity',
  DURATION = 'duration',
  AUTO_UNPUBLISH = 'autoUnpublish',
  VISIBILITY = 'visibility',
}

export const listingActivityFieldIds = Object.values(ListingActivityField);
export const ListingActivityFieldSchema = z.nativeEnum(ListingActivityField);

export enum ListingActivityCategory {
  PRICING = 'pricing',
  CONTENT = 'content',
  STATUS = 'status',
}

export const listingActivityCategories = Object.values(ListingActivityCategory);

export const listingActivityFieldDefinitions = {
  [ListingActivityField.TITLE]: { label: 'Title', category: ListingActivityCategory.CONTENT },
  [ListingActivityField.SUMMARY]: { label: 'Summary', category: ListingActivityCategory.CONTENT },
  [ListingActivityField.DESCRIPTION]: { label: 'Description', category: ListingActivityCategory.CONTENT },
  [ListingActivityField.CATEGORY]: { label: 'Category', category: ListingActivityCategory.CONTENT },
  [ListingActivityField.PRICING]: { label: 'Pricing', category: ListingActivityCategory.PRICING },
  [ListingActivityField.PHOTOS]: { label: 'Photos', category: ListingActivityCategory.CONTENT },
  [ListingActivityField.LOCATION]: { label: 'Location', category: ListingActivityCategory.CONTENT },
  [ListingActivityField.VIBES]: { label: 'Vibes', category: ListingActivityCategory.CONTENT },
  [ListingActivityField.HOST]: { label: 'Host', category: ListingActivityCategory.CONTENT },
  [ListingActivityField.BUTTON_LABEL]: { label: 'Book Button Label', category: ListingActivityCategory.CONTENT },
  [ListingActivityField.REMINDERS]: { label: 'Reminders', category: ListingActivityCategory.CONTENT },
  [ListingActivityField.CANCELLATION_POLICY]: { label: 'Cancellation policy', category: ListingActivityCategory.CONTENT },
  [ListingActivityField.RESCHEDULE_POLICY]: { label: 'Reschedule policy', category: ListingActivityCategory.CONTENT },
  [ListingActivityField.EMAIL_MESSAGE]: { label: 'Custom email message', category: ListingActivityCategory.CONTENT },
  [ListingActivityField.NOTIFICATION_EMAILS]: { label: 'Brand notifications', category: ListingActivityCategory.CONTENT },
  [ListingActivityField.MAP_RESOURCES]: { label: 'Map resources', category: ListingActivityCategory.CONTENT },
  [ListingActivityField.RESOURCE_MAP_IMAGE]: { label: 'Resource map image', category: ListingActivityCategory.CONTENT },
  [ListingActivityField.RESOURCE_GROUP]: { label: 'Resource Group', category: ListingActivityCategory.CONTENT },
  [ListingActivityField.RESOURCE_GROUP_ORDER]: { label: 'Resource Group order', category: ListingActivityCategory.CONTENT },
  [ListingActivityField.RESOURCE_CAPACITY]: { label: 'Resource capacity', category: ListingActivityCategory.CONTENT },
  [ListingActivityField.RESOURCES]: { label: 'Resources', category: ListingActivityCategory.CONTENT },
  [ListingActivityField.INCLUDED_ITEMS]: { label: 'Included items', category: ListingActivityCategory.CONTENT },
  [ListingActivityField.PAYMENT_METHODS]: { label: 'Payment methods', category: ListingActivityCategory.PRICING },
  [ListingActivityField.TAXES]: { label: 'Taxes & fees', category: ListingActivityCategory.PRICING },
  [ListingActivityField.ROOM_CHARGE_VALIDATION]: {
    label: 'Room charge validation',
    category: ListingActivityCategory.PRICING,
  },
  [ListingActivityField.MEMBER_NUMBER_VALIDATION]: {
    label: 'Member number validation',
    category: ListingActivityCategory.PRICING,
  },
  [ListingActivityField.COMPLIMENTARY_BOOKING_VALIDATION]: {
    label: 'Validate complimentary booking',
    category: ListingActivityCategory.PRICING,
  },
  [ListingActivityField.AVAILABILITY]: { label: 'Availability', category: ListingActivityCategory.CONTENT },
  [ListingActivityField.CAPACITY]: { label: 'Capacity', category: ListingActivityCategory.CONTENT },
  [ListingActivityField.DURATION]: { label: 'Duration', category: ListingActivityCategory.CONTENT },
  [ListingActivityField.AUTO_UNPUBLISH]: { label: 'Auto Unpublish', category: ListingActivityCategory.STATUS },
  [ListingActivityField.VISIBILITY]: { label: 'Visibility', category: ListingActivityCategory.STATUS },
} satisfies Record<ListingActivityField, { label: string; category: ListingActivityCategory }>;

export type ListingActivityPathDefinition = { label: string; field: ListingActivityField };

export const listingActivityFieldsForCategory = (category: ListingActivityCategory): ListingActivityField[] =>
  listingActivityFieldIds.filter((field) => listingActivityFieldDefinitions[field].category === category);
